(() => {
  const DEFAULT_STORAGE_KEY = 'roadrex-garage-v1';
  const VEHICLE_FIELDS = ['year', 'make', 'model', 'variant'];
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([type="hidden"]):not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const isElement = (value) => value instanceof Element;

  const getEventElement = (event) => {
    const target = event.composedPath?.()[0] || event.target;
    return isElement(target) ? target : null;
  };

  const vehicleLabel = (vehicle) => VEHICLE_FIELDS
    .map((field) => {
      const value = String(vehicle?.[field] || '').trim();
      if (field !== 'variant') return value;
      return /^(all|any)\b.*\b(variant|model|trim|fitment)/i.test(value) ? '' : value;
    })
    .filter(Boolean)
    .join(' ');

  class RoadRexAssistance extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.storageKey = this.dataset.storageKey || DEFAULT_STORAGE_KEY;
      this.shopUrl = this.dataset.shopUrl || '/collections/all';
      this.cartUrl = this.dataset.cartUrl || '/cart';
      this.emptyVehicleLabel = this.dataset.emptyVehicleLabel || 'No vehicle saved';
      this.drawers = [...this.querySelectorAll('[data-rr-assistance-drawer]')];
      this.scrim = this.querySelector('.rr-assistance-hub__scrim');
      this.controls = this.querySelector('[data-rr-assistance-controls]');
      this.modeButtons = [...this.querySelectorAll('[data-rr-enquiry-mode]')];
      this.modePanels = [...this.querySelectorAll('[data-rr-mode-panel]')];
      this.cartButtons = [...this.querySelectorAll('[data-rr-cart-open]')];
      this.cartCounts = [...this.querySelectorAll('[data-rr-cart-count]')];
      this.cartStatuses = [...this.querySelectorAll('[data-rr-cart-status]')];
      this.vehicleContextInputs = [...this.querySelectorAll('[data-rr-vehicle-context]')];
      this.visibleVehicleInputs = [...this.querySelectorAll('[data-rr-visible-vehicle]')];
      this.backgroundState = [];
      this.currentDrawer = null;
      this.opener = null;
      this.currentVehicle = null;

      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);
      this.handleDocumentFocus = this.handleDocumentFocus.bind(this);
      this.handleCartEvent = this.handleCartEvent.bind(this);
      this.handleCartDrawerOpen = this.handleCartDrawerOpen.bind(this);
      this.handleCartDrawerClose = this.handleCartDrawerClose.bind(this);
      this.handleVehicleEvent = this.handleVehicleEvent.bind(this);
      this.handleFitmentUnavailable = this.handleFitmentUnavailable.bind(this);
      this.handleFormSubmit = this.handleFormSubmit.bind(this);
      this.handleStorage = this.handleStorage.bind(this);
      this.handlePageShow = this.handlePageShow.bind(this);
      this.handleSectionSelect = this.handleSectionSelect.bind(this);
      this.handleSectionDeselect = this.handleSectionDeselect.bind(this);

      document.addEventListener('click', this.handleDocumentClick);
      document.addEventListener('keydown', this.handleDocumentKeydown, true);
      document.addEventListener('focusin', this.handleDocumentFocus, true);
      document.addEventListener('on:cart:add', this.handleCartEvent);
      document.addEventListener('on:line-item:change', this.handleCartEvent);
      document.addEventListener('rr:cart:updated', this.handleCartEvent);
      document.addEventListener('rr:cart:open-state', this.handleCartDrawerOpen);
      document.addEventListener('rr:cart:close-state', this.handleCartDrawerClose);
      document.addEventListener('rr:vehicle-change', this.handleVehicleEvent);
      document.addEventListener('rr:fitment-unavailable', this.handleFitmentUnavailable);
      window.addEventListener('storage', this.handleStorage);
      window.addEventListener('pageshow', this.handlePageShow);

      const nativeCartCount = document.querySelector('[data-cart-count]');
      if (nativeCartCount) {
        this.cartCountObserver = new MutationObserver(() => this.updateCartCount(Number(nativeCartCount.textContent) || 0));
        this.cartCountObserver.observe(nativeCartCount, { childList: true, characterData: true, subtree: true });
      }

      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:section:select', this.handleSectionSelect);
        document.addEventListener('shopify:section:deselect', this.handleSectionDeselect);
      }

      this.visibleVehicleInputs.forEach((input) => {
        input.addEventListener('input', () => {
          input.dataset.rrAutofilled = 'false';
        });
      });

      if (this.dataset.mobileDock === 'true') {
        document.documentElement.classList.add('rr-assistance-mobile-dock-enabled');
      }

      this.enquiryForm = this.querySelector('.rr-enquiry__form');
      this.modeStorageKey = `${this.storageKey}-enquiry-mode`;
      this.enquiryForm?.addEventListener('submit', this.handleFormSubmit);

      const formState = this.querySelector('[data-rr-form-state]')?.dataset.rrFormState;
      const storedMode = formState === 'success' || formState === 'error' ? this.readStoredMode() : '';
      const initialMode = storedMode || this.querySelector('[data-rr-enquiry-type]')?.value || 'Product enquiry';
      this.setMode(this.normalizeMode(initialMode));
      this.syncVehicle(this.readVehicle());
      this.updateCartCount(Number(nativeCartCount?.textContent) || 0);

      if (formState === 'success' || formState === 'error') {
        window.requestAnimationFrame(() => this.openDrawer('enquiry', null, { focusFeedback: true }));
      }
    }

    disconnectedCallback() {
      if (!this.initialized) return;

      document.removeEventListener('click', this.handleDocumentClick);
      document.removeEventListener('keydown', this.handleDocumentKeydown, true);
      document.removeEventListener('focusin', this.handleDocumentFocus, true);
      document.removeEventListener('on:cart:add', this.handleCartEvent);
      document.removeEventListener('on:line-item:change', this.handleCartEvent);
      document.removeEventListener('rr:cart:updated', this.handleCartEvent);
      document.removeEventListener('rr:cart:open-state', this.handleCartDrawerOpen);
      document.removeEventListener('rr:cart:close-state', this.handleCartDrawerClose);
      document.removeEventListener('rr:vehicle-change', this.handleVehicleEvent);
      document.removeEventListener('rr:fitment-unavailable', this.handleFitmentUnavailable);
      window.removeEventListener('storage', this.handleStorage);
      window.removeEventListener('pageshow', this.handlePageShow);
      this.cartCountObserver?.disconnect();
      document.removeEventListener('shopify:section:select', this.handleSectionSelect);
      document.removeEventListener('shopify:section:deselect', this.handleSectionDeselect);
      this.enquiryForm?.removeEventListener('submit', this.handleFormSubmit);

      this.closeDrawer({ restoreFocus: false });
      this.cartRequest?.abort();
      this.initialized = false;

      if (!document.querySelector('road-rex-assistance[data-mobile-dock="true"]')) {
        document.documentElement.classList.remove('rr-assistance-mobile-dock-enabled');
      }
    }

    handleDocumentClick(event) {
      const target = getEventElement(event);
      if (!target) return;

      const closeTrigger = target.closest('[data-rr-assistance-close]');
      if (closeTrigger && this.contains(closeTrigger)) {
        event.preventDefault();
        this.closeDrawer();
        return;
      }

      const modeTrigger = target.closest('[data-rr-enquiry-mode]');
      if (modeTrigger && this.contains(modeTrigger)) {
        event.preventDefault();
        this.setMode(modeTrigger.dataset.rrEnquiryMode);
        return;
      }

      const clearTrigger = target.closest('[data-rr-clear-garage]');
      if (clearTrigger && this.contains(clearTrigger)) {
        event.preventDefault();
        this.clearVehicle();
        return;
      }

      const finderTrigger = target.closest('[data-rr-find-vehicle]');
      if (finderTrigger && this.contains(finderTrigger)) {
        const finder = document.querySelector('road-rex-vehicle-finder');
        if (finder) {
          event.preventDefault();
          this.closeDrawer({ restoreFocus: false });
          window.requestAnimationFrame(() => this.revealFinder(finder));
        }
        return;
      }

      const garageTrigger = target.closest('[data-rr-garage-open]');
      if (garageTrigger) {
        event.preventDefault();
        this.openDrawer('garage', garageTrigger);
        return;
      }

      const cartTrigger = target.closest('[data-rr-cart-open]');
      if (cartTrigger && this.contains(cartTrigger)) {
        event.preventDefault();
        this.openCart(cartTrigger);
        return;
      }

      const enquiryTrigger = target.closest('[data-rr-enquiry-open]');
      if (enquiryTrigger) {
        event.preventDefault();
        const enquiryMode = this.normalizeMode(enquiryTrigger.dataset.queryType);
        this.setMode(enquiryMode);
        const productTitle = enquiryTrigger.dataset.productTitle;
        if (productTitle) {
          const productFieldByMode = {
            product: 'input[name="contact[Product enquiry]"]',
            fitment: 'input[name="contact[Product or part]"]',
            freight: 'input[name="contact[Freight product]"]',
            installation: 'input[name="contact[Installation request]"]',
            'complete-build': 'input[name="contact[Build goal]"]'
          };
          const productField = this.querySelector(productFieldByMode[enquiryMode]);
          if (productField) productField.value = productTitle;
        }
        const pageUrlField = this.querySelector('input[name="contact[Page URL]"]');
        if (pageUrlField) pageUrlField.value = window.location.href;
        this.openDrawer('enquiry', enquiryTrigger);
      }
    }

    handleDocumentKeydown(event) {
      if (!this.currentDrawer) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.closeDrawer();
        return;
      }

      const activeMode = getEventElement(event)?.closest?.('[data-rr-enquiry-mode]');
      if (activeMode && this.contains(activeMode) && ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        const index = this.modeButtons.indexOf(activeMode);
        let nextIndex = index;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (index + 1) % this.modeButtons.length;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (index - 1 + this.modeButtons.length) % this.modeButtons.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = this.modeButtons.length - 1;
        this.setMode(this.modeButtons[nextIndex]?.dataset.rrEnquiryMode, true);
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = this.getFocusable(this.currentDrawer);
      if (!focusable.length) {
        event.preventDefault();
        this.currentDrawer.focus({ preventScroll: true });
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || !this.currentDrawer.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !this.currentDrawer.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    }

    handleDocumentFocus(event) {
      if (!this.currentDrawer || this.currentDrawer.contains(event.target)) return;
      const fallback = this.getFocusable(this.currentDrawer)[0] || this.currentDrawer;
      fallback.focus({ preventScroll: true });
    }

    handleCartEvent(event) {
      const count = Number(event.detail?.cart?.item_count);
      if (Number.isFinite(count)) this.updateCartCount(count);
      else this.refreshCartCount();
    }

    handleCartDrawerOpen() {
      if (this.currentDrawer) this.closeDrawer({ restoreFocus: false });
      this.dataset.cartOpen = 'true';
      this.controls?.setAttribute('aria-hidden', 'true');
      this.controls?.setAttribute('inert', '');
    }

    handleCartDrawerClose() {
      delete this.dataset.cartOpen;
      if (!this.currentDrawer) {
        this.controls?.removeAttribute('aria-hidden');
        this.controls?.removeAttribute('inert');
      }
    }

    handleVehicleEvent(event) {
      this.syncVehicle(event.detail && typeof event.detail === 'object' ? event.detail : this.readVehicle());
    }

    handleFitmentUnavailable(event) {
      const detail = event.detail && typeof event.detail === 'object' ? event.detail : {};
      const vehicle = detail.vehicle && typeof detail.vehicle === 'object' ? detail.vehicle : this.readVehicle();
      const opener = detail.opener instanceof HTMLElement ? detail.opener : null;
      this.syncVehicle(vehicle);
      this.setMode('fitment');
      this.openDrawer('enquiry', opener);
    }

    handleFormSubmit() {
      const mode = this.querySelector('[data-rr-enquiry-type]')?.value || 'Fitment';
      try {
        window.sessionStorage.setItem(this.modeStorageKey, mode);
      } catch (_error) {
        // Form submission still works when session storage is unavailable.
      }
    }

    readStoredMode() {
      try {
        return window.sessionStorage.getItem(this.modeStorageKey) || '';
      } catch (_error) {
        return '';
      }
    }

    handleStorage(event) {
      if (event.key === this.storageKey || event.key === null) this.syncVehicle(this.readVehicle());
    }

    handlePageShow(event) {
      this.syncVehicle(this.readVehicle());
      if (event?.persisted) this.refreshCartCount();
    }

    handleSectionSelect(event) {
      if (event.target === this.closest('.shopify-section')) this.openDrawer('garage', null);
    }

    handleSectionDeselect(event) {
      if (event.target === this.closest('.shopify-section')) this.closeDrawer({ restoreFocus: false });
    }

    openDrawer(name, opener, options = {}) {
      const target = this.drawers.find((drawer) => drawer.dataset.rrAssistanceDrawer === name);
      if (!target) return;

      if (!this.currentDrawer && opener instanceof HTMLElement) this.opener = opener;
      this.drawers.forEach((drawer) => {
        const active = drawer === target;
        drawer.setAttribute('aria-hidden', String(!active));
        drawer.toggleAttribute('inert', !active);
      });

      this.currentDrawer = target;
      this.setAttribute('data-open', name);
      this.scrim?.setAttribute('aria-hidden', 'false');
      this.controls?.setAttribute('aria-hidden', 'true');
      this.controls?.setAttribute('inert', '');

      target.focus({ preventScroll: true });
      this.lockBackground();
      document.documentElement.classList.add('rr-assistance-is-open');

      window.requestAnimationFrame(() => {
        const feedback = options.focusFeedback ? target.querySelector('[data-rr-form-feedback]') : null;
        const focusTarget = feedback || this.getFocusable(target)[0] || target;
        focusTarget.focus({ preventScroll: true });
      });
    }

    closeDrawer(options = {}) {
      const { restoreFocus = true } = options;
      if (!this.currentDrawer && !this.hasAttribute('data-open')) return;

      this.drawers.forEach((drawer) => {
        drawer.setAttribute('aria-hidden', 'true');
        drawer.setAttribute('inert', '');
      });
      this.scrim?.setAttribute('aria-hidden', 'true');
      this.controls?.removeAttribute('aria-hidden');
      this.controls?.removeAttribute('inert');
      this.removeAttribute('data-open');
      this.currentDrawer = null;
      this.unlockBackground();
      document.documentElement.classList.remove('rr-assistance-is-open');

      const restoreTarget = this.opener;
      this.opener = null;
      if (restoreFocus && restoreTarget?.isConnected) {
        window.requestAnimationFrame(() => restoreTarget.focus({ preventScroll: true }));
      }
    }

    lockBackground() {
      if (this.backgroundState.length) return;

      [...document.body.children].forEach((element) => {
        if (element.contains(this) || ['SCRIPT', 'STYLE', 'LINK'].includes(element.tagName)) return;
        this.backgroundState.push({
          element,
          hadInert: element.hasAttribute('inert'),
          ariaHidden: element.getAttribute('aria-hidden')
        });
        element.setAttribute('inert', '');
        element.setAttribute('aria-hidden', 'true');
      });
    }

    unlockBackground() {
      this.backgroundState.forEach(({ element, hadInert, ariaHidden }) => {
        if (!element.isConnected) return;
        if (!hadInert) element.removeAttribute('inert');
        if (ariaHidden === null) element.removeAttribute('aria-hidden');
        else element.setAttribute('aria-hidden', ariaHidden);
      });
      this.backgroundState = [];
    }

    getFocusable(container) {
      return [...container.querySelectorAll(FOCUSABLE_SELECTOR)].filter((element) => {
        if (element.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
        const styles = window.getComputedStyle(element);
        return styles.visibility !== 'hidden' && styles.display !== 'none' && element.getClientRects().length > 0;
      });
    }

    setMode(mode, moveFocus = false) {
      const normalizedMode = this.modeButtons.some((button) => button.dataset.rrEnquiryMode === mode) ? mode : 'product';
      let activeValue = 'Product enquiry';

      this.modeButtons.forEach((button) => {
        const active = button.dataset.rrEnquiryMode === normalizedMode;
        button.setAttribute('aria-selected', String(active));
        button.tabIndex = active ? 0 : -1;
        if (active) {
          activeValue = button.dataset.modeValue || 'Product enquiry';
          if (moveFocus) button.focus();
        }
      });

      this.modePanels.forEach((panel) => {
        const active = panel.dataset.rrModePanel === normalizedMode;
        panel.hidden = !active;
        panel.toggleAttribute('inert', !active);
        panel.querySelectorAll('input, select, textarea').forEach((field) => {
          field.disabled = !active;
        });
      });

      this.querySelectorAll('[data-rr-enquiry-type]').forEach((input) => {
        input.value = activeValue;
      });
    }

    normalizeMode(value = '') {
      const text = String(value).toLowerCase();
      if (text.includes('freight') || text.includes('shipping')) return 'freight';
      if (text.includes('install') || text.includes('workshop') || text.includes('service')) return 'installation';
      if (text.includes('complete') || text.includes('build') || text.includes('package')) return 'complete-build';
      if (text.includes('product') || text.includes('availability') || text.includes('enquir')) return 'product';
      return 'fitment';
    }

    readVehicle() {
      try {
        const vehicle = JSON.parse(window.localStorage.getItem(this.storageKey) || 'null');
        if (!vehicle || typeof vehicle !== 'object' || Array.isArray(vehicle)) return null;
        return vehicleLabel(vehicle) ? vehicle : null;
      } catch (_error) {
        return null;
      }
    }

    syncVehicle(vehicle) {
      this.currentVehicle = vehicle && vehicleLabel(vehicle) ? vehicle : null;
      const label = vehicleLabel(this.currentVehicle);
      const loading = this.querySelector('[data-rr-garage-loading]');
      const saved = this.querySelector('[data-rr-garage-saved]');
      const empty = this.querySelector('[data-rr-garage-empty]');

      if (loading) loading.hidden = true;
      if (saved) saved.hidden = !this.currentVehicle;
      if (empty) empty.hidden = Boolean(this.currentVehicle);

      const values = {
        year: this.currentVehicle?.year || '—',
        make: this.currentVehicle?.make || '—',
        model: this.currentVehicle?.model || '—',
        variant: this.currentVehicle?.variant || '—'
      };

      Object.entries(values).forEach(([field, value]) => {
        this.querySelectorAll(`[data-rr-garage-${field}]`).forEach((node) => {
          node.textContent = value;
        });
      });

      this.querySelectorAll('[data-rr-garage-vehicle-label]').forEach((node) => {
        node.textContent = label;
      });

      this.querySelectorAll('[data-rr-garage-browse]').forEach((link) => {
        const hasVerifiedUrl = Boolean(this.currentVehicle?.url);
        link.hidden = !hasVerifiedUrl;
        link.toggleAttribute('inert', !hasVerifiedUrl);
        link.setAttribute('aria-hidden', String(!hasVerifiedUrl));
        if (hasVerifiedUrl) link.href = this.currentVehicle.url;
        else link.removeAttribute('href');
      });

      this.vehicleContextInputs.forEach((input) => {
        input.value = label;
      });

      this.visibleVehicleInputs.forEach((input) => {
        if (!input.value || input.dataset.rrAutofilled !== 'false') {
          input.value = label;
          input.dataset.rrAutofilled = label ? 'true' : '';
        }
      });

      document.querySelectorAll('[data-rr-current-vehicle]').forEach((node) => {
        node.textContent = label || node.dataset.rrEmptyLabel || this.emptyVehicleLabel;
      });
    }

    clearVehicle() {
      try {
        window.localStorage.removeItem(this.storageKey);
      } catch (_error) {
        // The UI still clears when storage is unavailable.
      }

      this.syncVehicle(null);
      const status = this.querySelector('[data-rr-garage-status]');
      if (status) status.textContent = this.dataset.removedMessage || 'Saved vehicle removed.';
      document.dispatchEvent(new CustomEvent('rr:vehicle-change', { detail: null }));
    }

    revealFinder(finder) {
      finder.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const mobileTrigger = finder.querySelector('[data-rr-finder-mobile-trigger]');
      const firstSelect = finder.querySelector('select:not([disabled])');

      if (window.matchMedia('(max-width: 768.98px)').matches && mobileTrigger?.getAttribute('aria-expanded') !== 'true') {
        mobileTrigger.click();
      } else {
        window.setTimeout(() => firstSelect?.focus({ preventScroll: true }), 420);
      }
    }

    openCart(opener) {
      this.closeDrawer({ restoreFocus: false });
      const drawer = document.querySelector('[data-rr-cart-drawer]');
      if (window.RoadRexCart && typeof window.RoadRexCart.open === 'function') {
        window.RoadRexCart.open(opener);
      } else if (drawer && typeof drawer.open === 'function') {
        drawer.open(opener);
      } else {
        window.location.assign(this.cartUrl);
      }
    }

    async refreshCartCount() {
      this.cartRequest?.abort();
      this.cartRequest = new AbortController();

      try {
        const endpoint = `${this.cartUrl.replace(/\/$/, '')}.js`;
        const response = await fetch(endpoint, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          signal: this.cartRequest.signal
        });
        if (!response.ok) throw new Error(String(response.status));
        const cart = await response.json();
        this.updateCartCount(Number(cart.item_count) || 0);
      } catch (error) {
        if (error.name !== 'AbortError') {
          const renderedCount = Number(this.cartCounts[0]?.textContent);
          if (Number.isFinite(renderedCount)) this.updateCartCount(renderedCount);
        }
      }
    }

    updateCartCount(count) {
      const safeCount = Math.max(0, Math.floor(Number(count) || 0));
      const displayCount = safeCount > 99 ? '99+' : String(safeCount);
      const itemLabel = safeCount === 1 ? (this.dataset.itemSingular || 'item') : (this.dataset.itemPlural || 'items');
      const inCartSuffix = this.dataset.inCartSuffix || 'in cart';

      this.cartCounts.forEach((badge) => {
        badge.textContent = displayCount;
        badge.hidden = safeCount === 0;
      });
      this.cartStatuses.forEach((status) => {
        status.textContent = `${safeCount} ${itemLabel} ${inCartSuffix}`;
      });
      this.cartButtons.forEach((button) => {
        const label = button.dataset.cartLabel || 'Cart';
        button.setAttribute('aria-label', `${label}, ${safeCount} ${itemLabel}`);
      });
    }
  }

  if (!customElements.get('road-rex-assistance')) {
    customElements.define('road-rex-assistance', RoadRexAssistance);
  }
})();
